/* eslint-disable react/no-unstable-nested-components */
import Header from '@/components/Header/Header';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FZBundleUpcharges.module.css';
import BundleUpchargesTable from './components/BundleUpchargeTable';
import { BundleUpchargeStoreGroup, Patches } from '@/types/bundles.types';
import Button from '@/components/Button/Button';
import { Row, RowSelectionState } from '@tanstack/react-table';
import _ from 'lodash';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useBundleUpchargeStoreGroups } from '@/services/bundles/bundles.service';
import { EditBundleUpchargesDrawer } from './components/EditBundleUpchargesDrawer';
import BundleUpchargesPriceDeviationDrawer from '@/pages/Franchisee/Bundles/BundleUpcharges/components/PriceDeviation/BundleUpchargePriceDeviationDrawer';
import { PriceInfo } from './components/PrinceInfo';

import { useExistsActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { patchesIncludesPrice } from '@/services/bundles/bundles.service.helper';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

export const printPrice = (patches: Patches[], uniqueHeader: { description: string; isLock: boolean }) => {
  const product = patches?.find((item: any) => item?.description === uniqueHeader.description);
  return product?.price ? `$${product.price}` : '';
};

const FZBundleUpcharges: FC = () => {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { data: bundleUpchargeData, refetch: refetchStoreGroups } = useBundleUpchargeStoreGroups(true);

  const { data: existsActiveGlobalFreeze } = useExistsActiveGlobalFreezes();

  const selectedStoreGroups = bundleUpchargeData?.storeGroups?.filter(
    (group: BundleUpchargeStoreGroup, index: number) => selectedRows[index],
  );

  const columnHeaders = bundleUpchargeData?.upchargePatches?.map((patchType) => ({
    description: patchType.description,
    isLock: patchType.isLock,
  }));

  const columnList = [
    {
      accessorFn: (originalRow: BundleUpchargeStoreGroup) => originalRow.storeGroupName,
      accessorKey: 'storeGroupName',
      header: 'Store Group',
      enableSorting: true,
      enableFilters: true,
      columnClassname: 'storeGroupColumn',
    } as any,
  ];

  const [bundleUpchargePriceDeviationData, setBundleUpchargePriceDeviationData] = useState({
    isBundleUpchargePriceDeviationOpen: false,
    cameFromEdit: false,
    type: '',
    groupId: '',
  });

  const handleBundleUpchargePriceDeviationDrawerClose = () => {
    setBundleUpchargePriceDeviationData({
      ...bundleUpchargePriceDeviationData,
      isBundleUpchargePriceDeviationOpen: false,
    });
  };

  const handleBundleUpchargePriceDeviationDrawerOpen = (type: string, groupId: string) => {
    setBundleUpchargePriceDeviationData((prevState) => ({
      ...prevState,
      isBundleUpchargePriceDeviationOpen: true,
      groupId,
      type,
    }));
  };

  const handleInfoClick = (product: any, storeGroup: any) => {
    handleBundleUpchargePriceDeviationDrawerOpen(product.type, storeGroup);
  };

  const uniqueColumnDescriptions = _.uniqBy(columnHeaders, (header) => header);
  uniqueColumnDescriptions.forEach((uniqueHeader, idx) => {
    columnList.push({
      accessorFn: (originalRow: BundleUpchargeStoreGroup) => printPrice(originalRow?.patches, uniqueHeader),
      id: uniqueHeader.description,
      enableSorting: true,
      columnClassname: idx === uniqueColumnDescriptions.length - 1 ? '' : 'dynamicHeader',
      sortingFn: 'dynamicSort',
      header: () => (
        <div className={styles.lockHeader}>
          {uniqueHeader.isLock && (
            <LockOutlined
              sx={{
                width: '16px',
                height: '16px',
                margin: '0px',
                padding: '0px',
                color: 'red',
              }}
            />
          )}
          {uniqueHeader.description}
        </div>
      ),
      enableFilters: true,
      filterFn: (row: Row<BundleUpchargeStoreGroup>, columnId: string, filterValue: string) =>
        patchesIncludesPrice(row.original.patches, filterValue, uniqueHeader),
      cell: PriceInfo(uniqueHeader.description, handleInfoClick),
    } as any);
  });

  return (
    <>
      <Header title={t('bundleUpcharges.title')} breadcrumb={{ label: t('bundleUpcharges.breadcrumb'), to: '/' }} />
      <BundleUpchargesTable
        enableRowSelection={!existsActiveGlobalFreeze}
        title={t('bundleUpcharges.storeGroupAssignments')}
        breadcrumb={{ label: t('bundleUpcharges.individualStores'), to: '/bundles/upcharges/stores' }}
        data={bundleUpchargeData?.storeGroups}
        columns={columnList}
        enableFilters
        onRowSelectionChange={setSelectedRows}
        selectedRows={selectedRows}
      />
      {
        <CalendarAlert
          className={styles.calendarAlert}
          requestedVerbiage={false}
          warningIcon
          addCalendarLink
        ></CalendarAlert>
      }
      {!existsActiveGlobalFreeze && (
        <div style={{ marginTop: '20px', marginLeft: '20px', paddingBottom: '150px' }}>
          <Button onClick={() => setEditDrawerOpen(true)} disabled={Object.keys(selectedRows).length < 1}>
            {t('bundleUpcharges.editBundleUpcharges')}
          </Button>
        </div>
      )}

      {editDrawerOpen && (
        <EditBundleUpchargesDrawer
          isOpen={editDrawerOpen}
          onClose={() => setEditDrawerOpen(false)}
          upchargePatches={bundleUpchargeData?.upchargePatches || []}
          storeGroups={selectedStoreGroups || []}
          stores={[]}
          afterSave={() => {
            setSelectedRows({});
            refetchStoreGroups();
          }}
        />
      )}

      {bundleUpchargePriceDeviationData.isBundleUpchargePriceDeviationOpen && (
        <BundleUpchargesPriceDeviationDrawer
          isOpen={bundleUpchargePriceDeviationData.isBundleUpchargePriceDeviationOpen}
          onClose={handleBundleUpchargePriceDeviationDrawerClose}
          type={bundleUpchargePriceDeviationData.type}
          groupId={bundleUpchargePriceDeviationData.groupId}
        />
      )}
    </>
  );
};

export default FZBundleUpcharges;
